import { useState } from 'react';
import { useAppContext } from '../../../providers';

import { SearchInput, SelectLocation } from '../../atoms';
import { ResourceQueuesDrawer, SelectionListValuesDrawer } from '../Drawers';
import { SetupCards } from '../SetupCards/SetupCards';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Button,
  Icon,
  toast,
} from '@pella/sash-framework';
import { ThreeDots } from 'react-loader-spinner';

import * as classes from './SetupAccordion.module.css';

/**
 * default filter for search input filters on description
 * @param  {Local.SetupCardData}  data - array objects to search through
 * @param {string} searchValue
 */
const defaultFilterFn = (data, searchValue) => {
  const q = searchValue.toLowerCase();
  return data?.filter((item) => item.description.toLowerCase().includes(q)) || [];
};

/**
 * Setup Accordion molecule
 * @param {Local.SetupAccordionProps} props - Component props
 */
export const SetupAccordion = ({
  id,
  title,
  data,
  isLoading,
  Drawer,
  cardConfig,
  searchFilterFn = defaultFilterFn,
}) => {
  const { resourceQueueSelectedId, selectionListSelectedId } = useAppContext();

  const [searchValue, setSearchValue] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [isCopyMode, setIsCopyMode] = useState(false);

  const filteredData = searchFilterFn(data, searchValue);
  const resourceQueueDrawerData = data?.find((item) => item.id === resourceQueueSelectedId) || undefined;
  const selectionListValuesDrawerData = data?.find((item) => item.id === selectionListSelectedId) || undefined;

  const handleCreateClick = () => {
    if (Drawer) {
      setDrawerOpen(true);
      setSelectedId('');
      setIsCopyMode(false);
    } else {
      toast.dismiss();
      toast.info(`Create ${title} - Not implemented yet`);
    }
  };

  /**
   * Handle edit click for all setup objects
   * @param {Local.SetupCardRecord} item
   */
  const handleEditClick = (item) => {
    if (Drawer) {
      setDrawerOpen(true);
      setSelectedId(item.id);
      setIsCopyMode(false);
    } else {
      toast.dismiss();
      toast.info(`Edit ${title} - Not implemented yet`);
    }
  };

  /**
   * Handle copy click for all setup objects
   * @param {Local.SetupCardRecord} item
   */
  const handleCopyClick = (item) => {
    if (Drawer) {
      setDrawerOpen(true);
      setSelectedId(item.id);
      setIsCopyMode(true);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedId('');
    setIsCopyMode(false);
  };

  return (
    <div>
      <Accordion id={id} data-testid={id} className={classes.accordion} variant='box'>
        <AccordionItem className={classes.accordionItem}>
          <AccordionTrigger>{title}</AccordionTrigger>
          <AccordionContent className={classes.accordionContent}>
            <div className={classes.searchOptions}>
              {title === 'Actions' && <SelectLocation hideInactive={false} />}
              <div className={classes.searchInput}>
                <SearchInput searchValue={searchValue} onChange={setSearchValue} />
              </div>
              <div className={classes.addButton}>
                <Button color='tertiary' onClick={handleCreateClick} disabled={!data}>
                  <Icon name='add' />
                  Create {title}
                </Button>
              </div>
            </div>
            {isLoading ? (
              <div data-testid={`${id}-loading`} className={classes.loading}>
                <ThreeDots color='black' width='50' />
              </div>
            ) : (
              <SetupCards
                data={filteredData}
                config={{
                  ...cardConfig,
                  onEdit: handleEditClick,
                  onCopy: cardConfig.canCopy ? handleCopyClick : undefined,
                }}
              />
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {Drawer && (
        <Drawer
          open={drawerOpen}
          close={handleDrawerClose}
          data={data}
          selectedId={selectedId}
          isCopyMode={isCopyMode}
        />
      )}
      {(cardConfig?.childSetups === 'queue' || cardConfig?.childSetups === 'resource') && (
        <ResourceQueuesDrawer data={resourceQueueDrawerData} type={cardConfig.childSetups} />
      )}
      {cardConfig?.childSetups === 'values' && <SelectionListValuesDrawer data={selectionListValuesDrawerData} />}
    </div>
  );
};
